import { useDarkMode } from "../ThemeContext";
import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";

import au1 from "../data/ambrishupnishad-1.json";

// Normalize tags from comma-separated string to array.
const ALL_DATA = au1.map((item) => ({
  ...item,
  tags: (item.tags || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean),
}));

const ACCENT = "#764ba2";
const ACCENT_LIGHT = "#f0e8ff";
const ACCENT_DARK = "#4a2472";
const gujaratiCollator = new Intl.Collator("gu-IN");
const LAST_READ_KEY = "ambrish-upnishad-last-read";


function writeCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const pair = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function buildItemKey(item) {
  return String(
    item?.filename || `${item?.year}-${item?.prakaran}-${item?.srno}`,
  );
}

function saveLastReadItem(item) {
  const payload = JSON.stringify({
    key: buildItemKey(item),
    year: item.year,
    prakaran: item.prakaran,
    srno: item.srno,
    chapterName: item.prakaran,
  });
  try {
    localStorage.setItem(LAST_READ_KEY, payload);
  } catch {
    // no-op
  }
  writeCookie(LAST_READ_KEY, payload);
}

function loadLastReadItem() {
  let raw = "";
  try {
    raw = localStorage.getItem(LAST_READ_KEY) || "";
  } catch {
    // no-op
  }
  if (!raw) raw = readCookie(LAST_READ_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (
      !parsed?.key &&
      (parsed?.year === undefined || parsed?.srno === undefined)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function buildTagFreq(items) {
  const freq = {};
  items.forEach((item) => {
    (item.tags || []).forEach((tag) => {
      if (tag) freq[tag] = (freq[tag] || 0) + 1;
    });
  });
  return freq;
}

function getSnippet(content, word) {
  const idx = content.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return "";
  const start = Math.max(0, idx - 80);
  const end = Math.min(content.length, idx + word.length + 80);
  let snippet = content.slice(start, end).replace(/\s+/g, " ").trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < content.length) snippet += "…";
  return snippet;
}

function getSnippetForCandidates(content, candidates) {
  const lower = content.toLowerCase();
  let best = null;

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const idx = lower.indexOf(candidate.toLowerCase());
    if (idx === -1) return;
    if (!best || idx < best.idx) {
      best = { idx, candidate };
    }
  });

  if (!best) return { snippet: "", hit: "" };
  return {
    snippet: getSnippet(content, best.candidate),
    hit: best.candidate,
  };
}

const ROMAN_WORD_OVERRIDES = {
  gunatit: "ગુણાતીત",
  gunatīt: "ગુણાતીત",
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
  "~": "ળ",
  "^": "ટ",
};

const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};

const GUJARATI_PHONETIC_EQUIVALENTS = [
  [/િ|ી/g, "િ"],
  [/ુ|ૂ/g, "ુ"],
  [/ે|ૈ/g, "ે"],
  [/ો|ૌ/g, "ો"],
  [/ઁ|ં|ઃ/g, "ં"],
  [/શ|ષ|સ/g, "સ"],
  [/ળ/g, "લ"],
];

const GUJARATI_INDEPENDENT_VOWELS = {
  અ: "a",
  આ: "aa",
  ઇ: "i",
  ઈ: "i",
  ઉ: "u",
  ઊ: "u",
  ઋ: "ri",
  એ: "e",
  ઐ: "ai",
  ઓ: "o",
  ઔ: "au",
};

const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

const GUJARATI_CONSONANTS = {
  ક: "k",
  ખ: "kh",
  ગ: "g",
  ઘ: "gh",
  ઙ: "ng",
  ચ: "ch",
  છ: "chh",
  જ: "j",
  ઝ: "jh",
  ઞ: "ny",
  ટ: "t",
  ઠ: "th",
  ડ: "d",
  ઢ: "dh",
  ણ: "n",
  ત: "t",
  થ: "th",
  દ: "d",
  ધ: "dh",
  ન: "n",
  પ: "p",
  ફ: "ph",
  બ: "b",
  ભ: "bh",
  મ: "m",
  ય: "y",
  ર: "r",
  લ: "l",
  વ: "v",
  શ: "sh",
  ષ: "sh",
  સ: "s",
  હ: "h",
  ળ: "l",
};

const GUJARATI_SIGNS = {
  "ં": "m",
  "ઃ": "h",
  "ઁ": "m",
};

const GUJARATI_VIRAMA = "્";

function isGujaratiText(text) {
  return /[\u0A80-\u0AFF]/.test(text || "");
}

function normalizeGujaratiPhonetic(text) {
  let normalized = (text || "").toLowerCase().replace(/\s+/g, "");
  GUJARATI_PHONETIC_EQUIVALENTS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

function normalizeRomanPhonetic(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function gujaratiToRomanPhonetic(text) {
  const input = text || "";
  let out = "";

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1] || "";

    if (c === "ક" && next === "્" && input[i + 2] === "ષ") {
      out += "ksh";
      i += 2;
      continue;
    }
    if (c === "જ" && next === "્" && input[i + 2] === "ઞ") {
      out += "gn";
      i += 2;
      continue;
    }

    if (GUJARATI_INDEPENDENT_VOWELS[c]) {
      out += GUJARATI_INDEPENDENT_VOWELS[c];
      continue;
    }

    if (GUJARATI_CONSONANTS[c]) {
      const base = GUJARATI_CONSONANTS[c];
      const nextChar = input[i + 1] || "";
      if (GUJARATI_MATRAS[nextChar]) {
        out += base + GUJARATI_MATRAS[nextChar];
        i += 1;
        continue;
      }
      if (nextChar === GUJARATI_VIRAMA) {
        out += base;
        i += 1;
        continue;
      }
      out += base + "a";
      continue;
    }

    if (GUJARATI_SIGNS[c]) {
      out += GUJARATI_SIGNS[c];
      continue;
    }

    out += " ";
  }

  out = out.replace(/a(\s|$)/g, "$1");
  return normalizeRomanPhonetic(out);
}

function transliterateRomanWordToGujarati(word) {
  const override = ROMAN_WORD_OVERRIDES[(word || "").toLowerCase()];
  if (override) return override;

  const input = (word || "")
    .replace(/L/g, "~")
    .replace(/T/g, "^")
    .toLowerCase()
    .replace(/[^a-z~^]/g, "");
  if (!input) return "";

  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );

  let out = "";
  let i = 0;
  while (i < input.length) {
    const remaining = input.slice(i);

    let vowelToken = "";
    for (const v of vowels) {
      if (remaining.startsWith(v)) {
        vowelToken = v;
        break;
      }
    }
    if (vowelToken) {
      out += ROMAN_VOWELS[vowelToken].independent;
      i += vowelToken.length;
      continue;
    }

    let consonantToken = "";
    for (const c of consonants) {
      if (remaining.startsWith(c)) {
        consonantToken = c;
        break;
      }
    }
    if (!consonantToken) {
      i += 1;
      continue;
    }

    out += ROMAN_CONSONANTS[consonantToken];
    i += consonantToken.length;

    const afterConsonant = input.slice(i);
    let followingVowel = "";
    for (const v of vowels) {
      if (afterConsonant.startsWith(v)) {
        followingVowel = v;
        break;
      }
    }
    if (followingVowel) {
      out += ROMAN_VOWELS[followingVowel].matra;
      i += followingVowel.length;
    }
  }

  return out;
}

function generateGujaratiLVariants(text) {
  const source = text || "";
  if (!/[લળ]/.test(source)) return [source];

  let variants = [""];
  for (const ch of source) {
    if (ch === "લ" || ch === "ળ") {
      const next = [];
      variants.forEach((prefix) => {
        next.push(prefix + "લ");
        next.push(prefix + "ળ");
      });
      variants = next;
      continue;
    }
    variants = variants.map((prefix) => prefix + ch);
  }
  return Array.from(new Set(variants));
}

function buildWordCandidates(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const candidates = new Set([q]);
  const lowered = q.toLowerCase();

  if (!isGujaratiText(q) && /^[a-z\s]+$/i.test(q)) {
    const transliterated = q
      .split(/\s+/)
      .map((w) => transliterateRomanWordToGujarati(w))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (transliterated) {
      generateGujaratiLVariants(transliterated).forEach((variant) => {
        candidates.add(variant);
        candidates.add(normalizeGujaratiPhonetic(variant));
      });
    }
    if (ROMAN_WORD_OVERRIDES[lowered]) {
      candidates.add(ROMAN_WORD_OVERRIDES[lowered]);
      candidates.add(normalizeGujaratiPhonetic(ROMAN_WORD_OVERRIDES[lowered]));
    }
  }

  if (isGujaratiText(q)) {
    candidates.add(normalizeGujaratiPhonetic(q));
  }

  return Array.from(candidates).filter(Boolean);
}

function Highlighted({ text, word }) {
  if (!(word || "").trim()) return <>{text}</>;
  const regex = new RegExp(
    `(${(word || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

export default function AmbrishUpnishad() {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedPrakaran, setSelectedPrakaran] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [fontSize, setFontSize] = useState(1);
  const [darkMode, setDarkMode] = useDarkMode();
  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState(null);
  const [wordSearching, setWordSearching] = useState(false);
  const [resultHighlightWord, setResultHighlightWord] = useState("");
  const [lastReadItem, setLastReadItem] = useState(() => loadLastReadItem());
  const activeRef = useRef(null);

  const years = useMemo(
    () => [...new Set(ALL_DATA.map((item) => item.year))].sort(),
    [],
  );

  const baseItems = useMemo(() => {
    if (selectedYear === null) return ALL_DATA;
    return ALL_DATA.filter((item) => item.year === selectedYear);
  }, [selectedYear]);

  const prakarans = useMemo(() => {
    return [...new Set(baseItems.map((item) => item.prakaran))].sort();
  }, [baseItems]);

  const tagFreq = useMemo(() => buildTagFreq(baseItems), [baseItems]);
  const sortedTags = useMemo(
    () =>
      Object.entries(tagFreq).sort(([first], [second]) =>
        gujaratiCollator.compare(first, second),
      ),
    [tagFreq],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return baseItems.filter((item) => {
      if (selectedPrakaran !== null && item.prakaran !== selectedPrakaran) {
        return false;
      }
      if (selectedTag && !(item.tags || []).includes(selectedTag)) {
        return false;
      }
      if (q) {
        return (
          item.title?.toLowerCase().includes(q) ||
          item.content?.toLowerCase().includes(q) ||
          item.subtitle?.toLowerCase().includes(q) ||
          item.desc?.toLowerCase().includes(q) ||
          item.gujdate?.includes(q) ||
          item.prakaran?.toLowerCase().includes(q) ||
          String(item.srno).includes(q)
        );
      }
      if (serialSearch.trim()) {
        if (!String(item.srno).includes(serialSearch.trim())) return false;
      }
      return true;
    });
  }, [baseItems, selectedPrakaran, selectedTag, search, serialSearch]);

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    const selectedKey = buildItemKey(selected);
    return filtered.findIndex((item) => buildItemKey(item) === selectedKey);
  }, [filtered, selected]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const openItem = useCallback((item) => {
    setSelected(item);
    setLastReadItem({
      key: buildItemKey(item),
      year: item.year,
      prakaran: item.prakaran,
      srno: item.srno,
      chapterName: item.prakaran,
    });
    saveLastReadItem(item);
    setFontSize(1);
  }, []);

  const clearWordSearch = () => {
    setWordQuery("");
    setWordResults(null);
    setResultHighlightWord("");
  };

  const loadAndSearch = useCallback((query) => {
    if (!query.trim()) return;
    setWordSearching(true);
    setWordResults(null);

    try {
      const q = query.trim();
      const ql = q.toLowerCase();
      const queryRomanNormalized = normalizeRomanPhonetic(query);
      const queryIsRoman =
        !isGujaratiText(query) && /^[a-z\s]+$/i.test(query.trim());
      const candidates = buildWordCandidates(query);
      const normalizedCandidates = candidates.map((c) =>
        normalizeGujaratiPhonetic(c),
      );

      const results = [];
      ALL_DATA.forEach((item) => {
        const content = [
          item.title || "",
          item.content || "",
          item.subtitle || "",
          item.desc || "",
          item.gujdate || "",
          item.prakaran || "",
          item.year || "",
        ].join(" ");

        const contentLower = content.toLowerCase();
        const contentPhonetic = normalizeGujaratiPhonetic(content);
        if (!item._romanPhoneticSearch) {
          item._romanPhoneticSearch = gujaratiToRomanPhonetic(content);
        }

        let matched = false;
        for (let i = 0; i < candidates.length; i += 1) {
          const candidate = candidates[i];
          const normalized = normalizedCandidates[i];
          if (
            (candidate && contentLower.includes(candidate.toLowerCase())) ||
            (normalized && contentPhonetic.includes(normalized))
          ) {
            matched = true;
            break;
          }
        }

        if (
          !matched &&
          queryIsRoman &&
          queryRomanNormalized &&
          item._romanPhoneticSearch.includes(queryRomanNormalized)
        ) {
          matched = true;
        }

        if (!matched && !contentLower.includes(ql)) return;

        const { snippet, hit } = getSnippetForCandidates(content, [
          ...candidates,
          q,
        ]);
        results.push({ item, snippet, hit: hit || q });
      });

      setWordResults(results);
      setResultHighlightWord(results[0]?.hit || q);
    } catch (err) {
      setWordResults([]);
      setResultHighlightWord(query.trim());
      console.error(err);
    } finally {
      setWordSearching(false);
    }
  }, []);

  const handleWordSearch = (e) => {
    e.preventDefault();
    loadAndSearch(wordQuery);
  };

  const handleResultClick = (item) => {
    openItem(item);
  };

  const goToRelativeItem = useCallback(
    (delta) => {
      if (!selected || selectedIndex < 0) return;
      const targetIndex = selectedIndex + delta;
      if (targetIndex < 0 || targetIndex >= filtered.length) return;
      const target = filtered[targetIndex];
      if (target) openItem(target);
    },
    [filtered, openItem, selected, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = (event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (!selected) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeItem(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeItem(1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativeItem, selected]);

  const openLastRead = useCallback(() => {
    if (!lastReadItem) return;
    const item = ALL_DATA.find((entry) => {
      const keyMatches = lastReadItem.key
        ? buildItemKey(entry) === String(lastReadItem.key)
        : false;
      const fallbackMatches =
        String(entry.year) === String(lastReadItem.year) &&
        String(entry.prakaran) === String(lastReadItem.prakaran) &&
        String(entry.srno) === String(lastReadItem.srno);
      return keyMatches || fallbackMatches;
    });
    if (item) openItem(item);
  }, [lastReadItem, openItem]);

  const contentHighlightWord =
    wordResults !== null ? (resultHighlightWord || wordQuery).trim() : "";

  return (
    <main
      className={`page-content pravachan-page swamini-vato-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          અંબરીષ ઉપનિષદ
          {filtered.length > 0 && (
            <span className="reader-muted" style={{ fontSize: "1rem", marginLeft: 12, color: "#888" }}>
              ({filtered.length})
            </span>
          )}
        </h1>
        <p className="header-subtitle">Ambrish Upnishad</p>
        {lastReadItem && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastRead}
            title="Open last read entry"
          >
            Last Read:{" "}
            {lastReadItem.chapterName || lastReadItem.prakaran} #
            {lastReadItem.srno}
          </button>
        )}
      </section>

      <form className="word-search-bar" onSubmit={handleWordSearch}>
        <input
          className="word-search-input"
          type="text"
          placeholder="Search word in all Ambrish Upnishad text... (Gujarati or English)"
          value={wordQuery}
          onChange={(e) => setWordQuery(e.target.value)}
        />
        <button
          type="submit"
          className="word-search-btn"
          disabled={wordSearching}
        >
          {wordSearching ? "Searching..." : "Search"}
        </button>
        <button
          type="button"
          className="word-search-clear"
          onClick={() => setDarkMode((d) => !d)}
          title="Toggle light/dark mode"
        >
          {darkMode ? "Light" : "Dark"}
        </button>
        {wordResults !== null && (
          <button
            type="button"
            className="word-search-clear"
            onClick={clearWordSearch}
          >
            x Clear
          </button>
        )}
      </form>

      {wordResults !== null && (
        <div className="word-results-panel">
          {wordResults.length === 0 ? (
            <p className="word-results-empty">
              No results found for "{wordQuery}".
            </p>
          ) : (
            <>
              <p className="word-results-count">
                {wordResults.length} result{wordResults.length !== 1 ? "s" : ""}{" "}
                for "{wordQuery}"
              </p>
              <ul className="word-results-list search-results-scroll-list">
                {wordResults.map(({ item, snippet }) => (
                  <li
                    key={`${buildItemKey(item)}-result`}
                    className={`word-result-item${
                      selected && buildItemKey(selected) === buildItemKey(item)
                        ? " active"
                        : ""
                    }`}
                    onClick={() => handleResultClick(item)}
                  >
                    <div className="word-result-header">
                      <span className="vachan-name">
                        {item.prakaran} #{item.srno}
                      </span>
                      <span className="vachan-name-guj">{item.title}</span>
                    </div>
                    <p className="word-result-snippet">
                      <Highlighted
                        text={snippet}
                        word={resultHighlightWord || wordQuery}
                      />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <section className="page-body">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div
            className="prasang-filter-list"
            role="group"
            aria-label="Years"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <button
              onClick={() => {
                setSelectedYear(null);
                setSelectedPrakaran(null);
                setSelectedTag(null);
              }}
              aria-pressed={selectedYear === null}
              style={yearBtnStyle()}
            >
              બધા (All)
            </button>
            {years.map((yr) => (
              <button
                key={yr}
                onClick={() => {
                  setSelectedYear(selectedYear === yr ? null : yr);
                  setSelectedPrakaran(null);
                  setSelectedTag(null);
                }}
                aria-pressed={selectedYear === yr}
                style={yearBtnStyle()}
              >
                {yr}
              </button>
            ))}
          </div>
        </div>

        {prakarans.length > 0 && selectedYear !== null && (
          <div
            className="prasang-filter-list prasang-filter-list--secondary"
            role="group"
            aria-label="Prakarans"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "center",
              marginBottom: 18,
            }}
          >
            <button
              onClick={() => setSelectedPrakaran(null)}
              aria-pressed={selectedPrakaran === null}
              style={prakaranBtnStyle()}
            >
              બધાં પ્રકરણ
            </button>
            {prakarans.map((pk) => (
              <button
                key={pk}
                onClick={() =>
                  setSelectedPrakaran(selectedPrakaran === pk ? null : pk)
                }
                aria-pressed={selectedPrakaran === pk}
                style={prakaranBtnStyle()}
              >
                {pk}
              </button>
            ))}
          </div>
        )}

        {sortedTags.length > 0 && (
          <div
            className="prasang-filter-list prasang-filter-list--cloud"
            role="group"
            aria-label="Tags"
            style={{
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 24,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: "0.85em",
                marginRight: 4,
              }}
            >
              Tags:
            </span>
            {sortedTags.map(([tag, freq]) => {
              const isActive = selectedTag === tag;
              return (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(isActive ? null : tag)}
                  aria-pressed={isActive}
                  style={{
                    fontSize: "0.85em",
                    padding: "3px 10px",
                    borderRadius: 16,
                    fontWeight: isActive ? 700 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    lineHeight: 1.5,
                  }}
                  title={`${freq} entries`}
                >
                  {tag}
                </button>
              );
            })}
            {selectedTag && (
              <button
                className="prasang-filter-clear"
                onClick={() => setSelectedTag(null)}
                style={{
                  fontSize: "0.78em",
                  padding: "3px 10px",
                  borderRadius: 16,
                  cursor: "pointer",
                }}
              >
                x Clear tag
              </button>
            )}
          </div>
        )}

        <div className="vachanamrut-body">
          <aside className="vachanamrut-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter by title or serial no..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedTag(null);
              }}
            />
            <input
              className="vachanamrut-search vachanamrut-search-serial"
              type="text"
              placeholder="Serial no search (e.g. 6 or 108)"
              value={serialSearch}
              onChange={(e) =>
                setSerialSearch(e.target.value.replace(/[^\d]/g, ""))
              }
            />
            <ul className="vachanamrut-list">
              {filtered.map((item) => {
                const isActive =
                  selected && buildItemKey(selected) === buildItemKey(item);
                const preview = [...(item.content || "")].slice(0, 20).join("");
                return (
                  <li
                    key={buildItemKey(item)}
                    ref={isActive ? activeRef : null}
                    className={`vachanamrut-item${isActive ? " active" : ""}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => openItem(item)}
                  >
                    <span className="vachan-name">
                      {item.prakaran} #{item.srno}
                    </span>
                    <span className="vachan-name-guj">{preview}...</span>
                    <span className="vachan-name-guj">{item.title}</span>
                  </li>
                );
              })}
            </ul>
            {filtered.length === 0 && (
              <p className="reader-muted" style={{ textAlign: "center", color: "#888", padding: 24 }}>
                No entries found.
              </p>
            )}
          </aside>

          <section className="vachanamrut-content-panel">
            {!selected ? (
              <div className="vachanamrut-placeholder">
                <p>વાંચવાં માટે ડાબી બાજુથી વાત પાસંદ કરો.</p>
                <p>Select an entry from the list to read.</p>
              </div>
            ) : (
              <article className="vachanamrut-article">
                <div
                  className="reader-font-controls"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 16,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => goToRelativeItem(-1)}
                    disabled={selectedIndex <= 0}
                    style={fontBtnStyle()}
                    title="Previous (Left Arrow)"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => goToRelativeItem(1)}
                    disabled={
                      selectedIndex === -1 ||
                      selectedIndex >= filtered.length - 1
                    }
                    style={fontBtnStyle()}
                    title="Next (Right Arrow)"
                  >
                    Next →
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.max(s - 0.15, 0.7))}
                    style={fontBtnStyle()}
                  >
                    A-
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.min(s + 0.15, 2.5))}
                    style={fontBtnStyle()}
                  >
                    A+
                  </button>
                  <span
                    style={{ color: "#999", fontSize: "0.78em", minWidth: 36 }}
                  >
                    {Math.round(fontSize * 100)}%
                  </span>
                </div>

                <div
                  className="reader-meta"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 18,
                    padding: "10px 14px",
                    background: "#fff8",
                    borderRadius: 10,
                    border: "1px solid #c8b8e8",
                    fontSize: `${fontSize}em`,
                  }}
                >
                  <MetaChip
                    label="પ્રકરણ"
                    value={selected.prakaran}
                    darkMode={darkMode}
                  />
                  <MetaChip
                    label="ક્રમ"
                    value={selected.srno}
                    darkMode={darkMode}
                  />
                  <MetaChip
                    label="તારીખ"
                    value={selected.gujdate}
                    darkMode={darkMode}
                  />
                  <MetaChip
                    label="Year"
                    value={selected.year}
                    darkMode={darkMode}
                  />
                </div>

                {selected.subtitle && (
                  <p
                    className="reader-accent"
                    style={{
                      fontSize: `${fontSize * 0.9}em`,
                      color: ACCENT_DARK,
                      whiteSpace: "pre-line",
                      marginBottom: 8,
                      fontStyle: "italic",
                    }}
                  >
                    <Highlighted
                      text={selected.subtitle || ""}
                      word={contentHighlightWord}
                    />
                  </p>
                )}

                {selected.desc && (
                  <p
                    className="reader-muted"
                    style={{
                      fontSize: `${fontSize * 0.85}em`,
                      color: "#777",
                      whiteSpace: "pre-line",
                      marginBottom: 14,
                    }}
                  >
                    <Highlighted
                      text={selected.desc || ""}
                      word={contentHighlightWord}
                    />
                  </p>
                )}

                <h2
                  className="vachan-title"
                  style={{ fontSize: `${fontSize * 1.1}em` }}
                >
                  <Highlighted
                    text={selected.title || ""}
                    word={contentHighlightWord}
                  />
                </h2>

                <div
                  className="reader-text"
                  style={{
                    color: darkMode ? "#fff" : "#1a1a2e",
                    lineHeight: 2,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "'Noto Sans Gujarati', sans-serif",
                    fontSize: `${fontSize}em`,
                  }}
                >
                  <Highlighted
                    text={selected.content || ""}
                    word={contentHighlightWord}
                  />
                </div>

                {selected.tags && selected.tags.length > 0 && (
                  <div
                    className="reader-tags"
                    style={{
                      marginTop: 20,
                      paddingTop: 14,
                      borderTop: `1px solid ${darkMode ? "#5d5d7a" : "#c8b8e8"}`,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    {selected.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: "0.78em",
                          padding: "2px 10px",
                          borderRadius: 12,
                          border: `1px solid ${darkMode ? "#9ea6ff" : `${ACCENT}88`}`,
                          color: darkMode ? "#eef1ff" : ACCENT_DARK,
                          background: darkMode ? "#2f3558" : ACCENT_LIGHT,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}

function yearBtnStyle() {
  return {
    padding: "6px 18px",
    borderRadius: 20,
    fontWeight: 600,
    fontSize: "0.88em",
    cursor: "pointer",
    transition: "all 0.15s",
  };
}

function prakaranBtnStyle() {
  return {
    padding: "5px 14px",
    borderRadius: 16,
    fontWeight: 600,
    fontSize: "0.83em",
    cursor: "pointer",
    transition: "all 0.15s",
  };
}

function fontBtnStyle() {
  return {
    padding: "2px 8px",
    fontSize: "0.82em",
    fontWeight: 700,
    cursor: "pointer",
    background: "#e8f0fe",
    border: `1px solid ${ACCENT}`,
    borderRadius: 5,
    color: ACCENT,
  };
}

function MetaChip({ label, value, darkMode = false }) {
  return (
    <span
      className="reader-meta-chip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: "0.82em",
        padding: "3px 10px",
        borderRadius: 8,
        background: darkMode ? "#2f3558" : ACCENT_LIGHT,
        border: `1px solid ${darkMode ? "#9ea6ff" : `${ACCENT}88`}`,
        color: darkMode ? "#eef1ff" : "#333",
      }}
    >
      <strong style={{ color: darkMode ? "#bfc6ff" : ACCENT }}>{label}:</strong>{" "}
      {value}
    </span>
  );
}
